import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Share2,
  Copy,
  Mail,
  ExternalLink,
  MapPin,
  RefreshCw,
  FileText,
  Check,
  ChevronDown,
  MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASK_SHORT = {
  Keeps: "K", KeeperBuddy: "KB", Drives: "R", Referees: "F", Coaches: "C", Coacht: "C", Rijdt: "R", Activity: "A", Unavailable: "N/A",
};

const TASK_LABELS = {
  Keeps: "Keept", KeeperBuddy: "K-Buddy", Drives: "Rijdt", Referees: "Fluit", Coaches: "Coacht", Coacht: "Coacht", Rijdt: "Rijdt", Activity: "Activiteit", Unavailable: "Afwezig",
};

const TASK_COLORS = {
  Keeps: { bg: "bg-blue-500", text: "text-blue-700", light: "bg-blue-50", border: "border-blue-200" },
  KeeperBuddy: { bg: "bg-yellow-400", text: "text-yellow-800", light: "bg-yellow-50", border: "border-yellow-200" },
  Drives: { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-50", border: "border-amber-200" },
  Referees: { bg: "bg-purple-500", text: "text-purple-700", light: "bg-purple-50", border: "border-purple-200" },
  Coaches: { bg: "bg-emerald-500", text: "text-emerald-700", light: "bg-emerald-50", border: "border-emerald-200" },
  Coacht: { bg: "bg-emerald-500", text: "text-emerald-700", light: "bg-emerald-50", border: "border-emerald-200" },
  Rijdt: { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-50", border: "border-amber-200" },
  Activity: { bg: "bg-indigo-500", text: "text-indigo-700", light: "bg-indigo-50", border: "border-indigo-200" },
  Unavailable: { bg: "bg-red-500", text: "text-red-700", light: "bg-red-50", border: "border-red-200" },
};

// Legenda items — deduplicated (Drives=Rijdt, Coaches=Coacht are same task)
const LEGEND_ITEMS = [
  { key: "Keeps", short: "K", label: "Keept" },
  { key: "KeeperBuddy", short: "KB", label: "K-Buddy" },
  { key: "Drives", short: "R", label: "Rijdt" },
  { key: "Referees", short: "F", label: "Fluit" },
  { key: "Coaches", short: "C", label: "Coacht" },
  { key: "Activity", short: "A", label: "Activiteit" },
];

const parseTasks = (taskString) => {
  if (!taskString || taskString === "-" || taskString === "Unavailable") return [];
  return taskString.split(", ").filter(t => t && t !== "-");
};

const TaskCell = ({ task }) => {
  if (task === "Unavailable") {
    return (
      <div className="w-full h-6 rounded bg-red-500 flex items-center justify-center">
        <span className="text-[10px] text-white font-medium">N/A</span>
      </div>
    );
  }
  
  const tasks = parseTasks(task);
  
  if (tasks.length === 0) {
    return <span className="text-slate-300 text-xs">–</span>;
  }
  
  if (tasks.length === 1) {
    const colors = TASK_COLORS[tasks[0]] || TASK_COLORS.Keeps;
    return (
      <div className={cn("w-full h-6 rounded flex items-center justify-center", colors.bg)}>
        <span className="text-[10px] font-semibold text-white">{TASK_SHORT[tasks[0]]}</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center justify-center gap-0.5 h-6">
      {tasks.map((t) => {
        const colors = TASK_COLORS[t] || TASK_COLORS.Keeps;
        return (
          <div 
            key={t}
            className={cn("h-5 flex-1 flex items-center justify-center rounded-sm", colors.bg)}
          >
            <span className="text-[9px] font-bold text-white">{TASK_SHORT[t]}</span>
          </div>
        );
      })}
    </div>
  );
};

export const Overview = () => {
  const { seasonPartId: urlSeasonPartId } = useParams();
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [seasonPartId, setSeasonPartId] = useState(urlSeasonPartId);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [addressModal, setAddressModal] = useState(null);
  const [gameCard, setGameCard] = useState(null);

  useEffect(() => {
    fetchOverview();
  }, [urlSeasonPartId]);

  const fetchOverview = async () => {
    try {
      const seasonsRes = await axios.get(`${API}/seasons`);
      if (seasonsRes.data.length === 0) {
        toast.error("Geen seizoen gevonden");
        navigate("/season");
        return;
      }
      
      const currentSeason = seasonsRes.data[0];
      setSeason(currentSeason);
      
      // Use URL param or default to first season part
      const partId = urlSeasonPartId || currentSeason.season_parts?.[0]?.id;
      if (!partId) {
        toast.error("Geen seizoensdelen gevonden");
        navigate("/season");
        return;
      }
      setSeasonPartId(partId);
      
      const response = await axios.get(`${API}/overview/season/${currentSeason.id}/part/${partId}`);
      setOverview(response.data);
      setShareToken(response.data.share_token);
    } catch (error) {
      toast.error("Overzicht laden mislukt");
    } finally {
      setLoading(false);
    }
  };

  const handleSeasonPartChange = (partId) => {
    setSeasonPartId(partId);
    navigate(`/overview/${partId}`);
  };

  const handleGenerateShareLink = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/overview/generate`, {
        season_id: overview.season.id,
        season_part_id: seasonPartId
      });
      setShareToken(response.data.token);
      toast.success(response.data.message);
      setShowShareModal(true);
    } catch (error) {
      toast.error("Deellink genereren mislukt");
    } finally {
      setGenerating(false);
    }
  };

  const getShareUrl = () => {
    return `${window.location.origin}/share/${shareToken}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      toast.success("Link gekopieerd naar klembord!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Link kopiëren mislukt");
    }
  };

  const handleShareWhatsApp = () => {
    const text = `Bekijk hier de planning van ${overview.season.team_name}:\n${getShareUrl()}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const handleShareEmail = () => {
    const subject = `${overview.season.club_name} - ${overview.season_part?.name} Planning`;
    const body = `Hi,\n\nHere's the match planning for ${overview.season.team_name}:\n\n${getShareUrl()}\n\nBest regards`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
  };

  const DUTCH_DAYS = ["ZONDAG", "MAANDAG", "DINSDAG", "WOENSDAG", "DONDERDAG", "VRIJDAG", "ZATERDAG"];
  const DUTCH_MONTHS = ["JANUARI", "FEBRUARI", "MAART", "APRIL", "MEI", "JUNI", "JULI", "AUGUSTUS", "SEPTEMBER", "OKTOBER", "NOVEMBER", "DECEMBER"];

  const getUpcomingMatch = () => {
    if (!overview?.matches) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return overview.matches
      .filter(m => m.type !== "activity" && m.date && new Date(m.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null;
  };

  const handleSendGameCard = () => {
    const match = getUpcomingMatch();
    if (!match) {
      toast.error("Geen aankomende wedstrijd gevonden");
      return;
    }

    const d = new Date(match.date);
    const dayName = DUTCH_DAYS[d.getDay()];
    const day = d.getDate();
    const monthName = DUTCH_MONTHS[d.getMonth()];

    const homeAway = match.is_home ? "THUIS" : "UIT";
    const city = match.address?.city || "";
    const street = match.address?.street || "";
    const locationLine = city && street ? `${city} (${street})` : city || street || "ntb";

    const shirt = match.shirt === "Home" ? "Thuis shirt" : match.shirt === "Away" ? "Uit shirt" : match.shirt || "ntb";
    const gather = match.gather_time || "ntb";
    const start = match.start_time || "ntb";

    const playerMap = {};
    overview.players.forEach(p => {
      // Match column-header naming: prefer last_name, fall back to first_name, then full name.
      playerMap[p.id] = (p.last_name || p.first_name || p.name || "").trim();
    });
    const managerMap = {};
    (overview.managers || []).forEach(m => {
      // Match player convention: last_name → first_name → full name.
      const label = (m.last_name || m.first_name || m.name || "").trim();
      managerMap[m.user_id] = label;
    });

    // Drivers: can be assigned via either naming convention (Drives = player-style, Rijdt = manager-style).
    // Both player_tasks and manager_tasks may use either value depending on how the task was set.
    const isDriveTask = (t) => t?.includes("Drives") || t?.includes("Rijdt");
    const isCoachTask = (t) => t?.includes("Coaches") || t?.includes("Coacht");
    const keepers = match.tasks.filter(t => t.task?.includes("Keeps")).map(t => playerMap[t.player_id]).filter(Boolean);
    const playerDrivers = match.tasks.filter(t => isDriveTask(t.task)).map(t => playerMap[t.player_id]).filter(Boolean);
    const managerDrivers = (match.manager_tasks || []).filter(t => isDriveTask(t.task)).map(t => managerMap[t.user_id]).filter(Boolean);
    const drivers = [...managerDrivers, ...playerDrivers];
    const managerCoaches = (match.manager_tasks || []).filter(t => isCoachTask(t.task)).map(t => managerMap[t.user_id]).filter(Boolean);
    const playerCoaches = match.tasks.filter(t => isCoachTask(t.task)).map(t => playerMap[t.player_id]).filter(Boolean);
    const coaches = [...managerCoaches, ...playerCoaches];
    const unavailable = match.tasks.filter(t => t.task === "Unavailable").map(t => playerMap[t.player_id]).filter(Boolean);

    let lines = [
      `${dayName} ${day} ${monthName}`,
      `${homeAway} tegen ${match.opponent_club}`,
      "",
      `\u{1F4CD} ${locationLine}`,
      `\u{23F0} Verzamelen: ${gather}`,
      `\u{23F0} Spelen: ${start}`,
      `\u{1F455} Tenue: ${shirt}`,
    ];

    if (keepers.length > 0) lines.push(`\u{1F9E4} Keepen: ${keepers.join(" / ")}`);
    const referees = match.tasks.filter(t => t.task?.includes("Referees")).map(t => playerMap[t.player_id]).filter(Boolean);
    if (referees.length > 0) lines.push(`\u{1F4E3} Fluiten: ${referees.join(" / ")}`);
    if (coaches.length > 0) lines.push(`\u{1F3D1} Coachen: ${coaches.join(" / ")}`);
    if (drivers.length > 0) lines.push(`\u{1F697} Rijden: ${drivers.join(" / ")}`);
    if (unavailable.length > 0) {
      const joined = unavailable.length === 1 ? unavailable[0] :
        unavailable.slice(0, -1).join(", ") + " en " + unavailable[unavailable.length - 1];
      lines.push(`\u{274C} Niet aanwezig: ${joined}`);
    }

    setGameCard(lines.join("\n"));
  };

  const handleCopyGameCard = async () => {
    try {
      const blob = new Blob([gameCard], { type: "text/plain;charset=utf-8" });
      await navigator.clipboard.write([new ClipboardItem({ "text/plain": blob })]);
      toast.success("Wedstrijdkaart gekopieerd!");
    } catch {
      // Fallback
      try {
        await navigator.clipboard.writeText(gameCard);
        toast.success("Wedstrijdkaart gekopieerd!");
      } catch {
        toast.error("Kopiëren mislukt");
      }
    }
  };

  const handleGameCardWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(gameCard)}`, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  if (!overview) return null;

  const currentPart = season?.season_parts?.find(p => p.id === seasonPartId);

  return (
    <div data-testid="overview-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-blue-600 mb-1">
            {overview.season.club_name} / {overview.season.team_name}
          </p>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Overzicht
          </h1>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-slate-500 mt-1 -ml-2 h-auto py-1 px-2">
                {currentPart?.name || "Selecteer seizoensdeel"}
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {season?.season_parts?.map(part => (
                <DropdownMenuItem 
                  key={part.id}
                  onClick={() => handleSeasonPartChange(part.id)}
                  className={cn(part.id === seasonPartId && "bg-slate-100")}
                >
                  {part.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleSendGameCard}
            variant="outline"
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
            data-testid="game-card-btn"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Wedstrijdkaart
          </Button>
          {shareToken ? (
            <Button
              onClick={() => setShowShareModal(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Overzicht Delen
            </Button>
          ) : (
            <Button
              onClick={handleGenerateShareLink}
              disabled={generating}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="generate-share-btn"
            >
              {generating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Deellink Genereren
            </Button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-slate-500">Legenda:</span>
        {LEGEND_ITEMS.map(item => {
          const colors = TASK_COLORS[item.key];
          return (
            <div key={item.key} className="flex items-center gap-1.5">
              <div className={cn("w-5 h-5 rounded flex items-center justify-center", colors.bg)}>
                <span className="text-white text-xs font-bold">{item.short}</span>
              </div>
              <span className="text-xs text-slate-600">{item.label}</span>
            </div>
          );
        })}
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded bg-red-500 flex items-center justify-center">
            <span className="text-white text-[9px] font-bold">N/A</span>
          </div>
          <span className="text-xs text-slate-600">Afwezig</span>
        </div>
      </div>

      {/* Overview Table */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
        <table className="w-full bg-white" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap bg-slate-50" style={{ position: "sticky", left: 0, zIndex: 20, minWidth: 80 }}>Datum</th>
              <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap bg-slate-50 sticky-col-tu-header" style={{ minWidth: 56 }}>T/U</th>
              <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap bg-slate-50 border-r border-slate-200 sticky-col-teg-header" style={{ minWidth: 128 }}>Tegenstander</th>
              <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap" style={{ minWidth: 80 }}>Team</th>
              <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap" style={{ minWidth: 80 }}>Adres</th>
              <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap" style={{ minWidth: 64 }}>Tenue</th>
              <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap" style={{ minWidth: 80 }}>Verzamelen</th>
              <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap" style={{ minWidth: 64 }}>Aanvang</th>
              {overview.players.map(player => {
                const label = (player.last_name || player.first_name || player.name || "").trim();
                const short = label.length > 8 ? `${label.substring(0, 8)}…` : label;
                return (
                  <th
                    key={player.id}
                    className="text-center text-xs uppercase font-medium text-slate-500 tracking-wider px-2 py-3 whitespace-nowrap"
                    style={{ minWidth: 72, maxWidth: 72, width: 72 }}
                    title={player.name}
                  >
                    {short}
                  </th>
                );
              })}
              {(overview.managers || []).length > 0 && (
                <th className="text-center text-xs uppercase font-medium text-slate-400 tracking-wider px-1 py-3 whitespace-nowrap border-l-2 border-slate-300" style={{ minWidth: 4, maxWidth: 4, width: 4 }}></th>
              )}
              {(overview.managers || []).map(mgr => {
                const fullName = (mgr.name || "").trim();
                const label = (mgr.last_name || mgr.first_name || fullName.split(" ")[0] || "").trim();
                const short = label.length > 8 ? `${label.substring(0, 8)}…` : label;
                return (
                  <th key={mgr.user_id} className="text-center text-xs uppercase font-medium text-emerald-600 tracking-wider px-2 py-3 whitespace-nowrap" style={{ minWidth: 72, maxWidth: 72, width: 72 }} title={fullName}>
                    {short}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {overview.matches.map((row, index) => {
              if (row.type === "activity") {
                // Activity row - individual cells to avoid sticky CSS conflicts
                return (
                  <tr key={row.match_id} data-testid={`overview-row-${index}`} style={{ backgroundColor: "#f5f3ff" }}>
                    <td className="px-3 py-2 text-sm text-slate-700 whitespace-nowrap" style={{ position: "sticky", left: 0, zIndex: 10, backgroundColor: "#f5f3ff", minWidth: 80 }}>
                      {row.date ? format(new Date(row.date), "dd MMM") : "-"}
                    </td>
                    <td colSpan={2} className="px-3 py-2 text-sm font-medium text-indigo-700 whitespace-nowrap truncate border-r border-slate-100" style={{ position: "sticky", left: 80, zIndex: 10, backgroundColor: "#f5f3ff", maxWidth: 184 }}>
                      {row.description}
                    </td>
                    <td style={{ backgroundColor: "#f5f3ff" }}></td>
                    <td style={{ backgroundColor: "#f5f3ff" }}></td>
                    <td style={{ backgroundColor: "#f5f3ff" }}></td>
                    <td style={{ backgroundColor: "#f5f3ff" }}></td>
                    <td className="px-3 py-2 text-sm text-indigo-600 font-medium whitespace-nowrap" style={{ minWidth: 64, backgroundColor: "#f5f3ff" }}>
                      {row.time || ""}
                    </td>
                    {(row.tasks || []).map(task => (
                      <td key={task.player_id} className="px-1 py-2" style={{ minWidth: 72, maxWidth: 72, width: 72, backgroundColor: "#f5f3ff" }}>
                        <TaskCell task={task.task} />
                      </td>
                    ))}
                    {(overview.managers || []).length > 0 && (
                      <td className="border-l-2 border-slate-300" style={{ minWidth: 4, maxWidth: 4, width: 4, backgroundColor: "#f5f3ff" }}></td>
                    )}
                    {(row.manager_tasks || []).map(mt => (
                      <td key={mt.user_id} className="px-1 py-2" style={{ minWidth: 72, maxWidth: 72, width: 72, backgroundColor: "#f5f3ff" }}>
                        <TaskCell task={mt.task} />
                      </td>
                    ))}
                  </tr>
                );
              }

              // Match row (existing)
              const match = row;
              const bg = match.is_home ? "#ffffff" : "#f8fafc";
              const bgClass = match.is_home ? "bg-white" : "bg-slate-50";
              return (
                <tr key={match.match_id} className={bgClass} data-testid={`overview-row-${index}`}>
                  <td className="px-3 py-2 text-sm text-slate-700 whitespace-nowrap" style={{ position: "sticky", left: 0, zIndex: 10, backgroundColor: bg, minWidth: 80 }}>
                    {match.date ? format(new Date(match.date), "dd MMM") : "-"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap sticky-col-tu" style={{ backgroundColor: bg, minWidth: 56 }}>
                    <span className={cn("text-xs font-medium", match.is_home ? "text-emerald-700" : "text-amber-700")}>
                      {match.is_home ? "Thuis" : "Uit"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700 whitespace-nowrap truncate border-r border-slate-100 sticky-col-teg" style={{ backgroundColor: bg, minWidth: 128, maxWidth: 128 }}>
                    {match.opponent_club}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-500 truncate whitespace-nowrap" style={{ minWidth: 80 }}>
                    {match.opponent_team}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" style={{ minWidth: 80 }}>
                    {match.address ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700"
                        onClick={() => setAddressModal(match.address)}
                      >
                        <MapPin className="w-3 h-3 mr-1" />
                        Bekijk
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" style={{ minWidth: 64 }}>
                    <span className={cn("text-xs font-medium", match.shirt === "Home" ? "text-emerald-700" : "text-amber-700")}>
                      {match.shirt === "Home" ? "Thuis" : match.shirt === "Away" ? "Uit" : match.shirt}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700 whitespace-nowrap" style={{ minWidth: 80 }}>
                    {match.gather_time || <span className="text-slate-400 italic">ntb</span>}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700 whitespace-nowrap" style={{ minWidth: 64 }}>
                    {match.start_time || <span className="text-slate-400 italic">ntb</span>}
                  </td>
                  {match.tasks.map(task => (
                    <td key={task.player_id} className="px-1 py-2" style={{ minWidth: 72, maxWidth: 72, width: 72 }}>
                      <TaskCell task={task.task} />
                    </td>
                  ))}
                  {(overview.managers || []).length > 0 && (
                    <td className="border-l-2 border-slate-300" style={{ minWidth: 4, maxWidth: 4, width: 4 }}></td>
                  )}
                  {(match.manager_tasks || []).map(mt => (
                    <td key={mt.user_id} className="px-1 py-2" style={{ minWidth: 72, maxWidth: 72, width: 72 }}>
                      <TaskCell task={mt.task} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent data-testid="share-modal">
          <DialogHeader>
            <DialogTitle>Planning Overzicht Delen</DialogTitle>
            <DialogDescription>
              Deel deze planning met ouders en spelers
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="flex gap-2">
              <Input 
                value={getShareUrl()} 
                readOnly 
                className="flex-1 text-sm"
                data-testid="share-url-input"
              />
              <Button
                variant="outline"
                onClick={handleCopyLink}
                className="flex-shrink-0"
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={handleShareWhatsApp}
                className="flex items-center justify-center gap-2 h-12 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                data-testid="share-whatsapp-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={handleShareEmail}
                className="flex items-center justify-center gap-2 h-12"
                data-testid="share-email-btn"
              >
                <Mail className="w-5 h-5" />
                Email
              </Button>
            </div>
            
            <div className="pt-2">
              <Button
                variant="link"
                className="w-full text-sm text-slate-500"
                onClick={() => window.open(getShareUrl(), "_blank")}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in nieuw tabblad
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Address Modal */}
      <Dialog open={!!addressModal} onOpenChange={() => setAddressModal(null)}>
        <DialogContent data-testid="address-modal">
          <DialogHeader>
            <DialogTitle>Wedstrijdlocatie</DialogTitle>
            <DialogDescription>Clubadres details</DialogDescription>
          </DialogHeader>
          {addressModal && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Straat:</span>{" "}
                  <span className="text-slate-900">{addressModal.street || "N/B"}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Postcode:</span>{" "}
                  <span className="text-slate-900">{addressModal.postal_code || "N/B"}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Plaats:</span>{" "}
                  <span className="text-slate-900">{addressModal.city || "N/B"}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Land:</span>{" "}
                  <span className="text-slate-900">{addressModal.country || "N/B"}</span>
                </p>
              </div>
              {addressModal.google_maps_url && (
                <Button
                  className="w-full"
                  onClick={() => window.open(addressModal.google_maps_url, "_blank")}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Bekijk op Google Maps
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Game Card Dialog */}
      <Dialog open={!!gameCard} onOpenChange={(open) => !open && setGameCard(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Wedstrijdkaart</DialogTitle>
            <DialogDescription>
              Kopieer de wedstrijdkaart of deel via WhatsApp
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <pre className="whitespace-pre-wrap text-sm bg-slate-50 rounded-lg p-4 border border-slate-200 font-sans leading-relaxed" data-testid="game-card-text">
              {gameCard}
            </pre>
            <div className="space-y-3">
              <Button
                onClick={handleCopyGameCard}
                className="w-full flex items-center justify-center gap-2 h-12 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="copy-game-card-btn"
              >
                <Copy className="w-4 h-4" />
                Kopieer naar klembord
              </Button>
              <Button
                variant="outline"
                onClick={handleGameCardWhatsApp}
                className="w-full flex items-center justify-center gap-2 h-12 border-green-200 text-green-700 hover:bg-green-50"
                data-testid="game-card-whatsapp-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Deel via WhatsApp
              </Button>
              <p className="text-xs text-slate-400 text-center">WhatsApp werkt op je telefoon of via de gedeployde app</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
